'use client';

import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import Image from 'next/image';
import { useEffect, useLayoutEffect, useRef, memo } from 'react';
import AnimatedLink from '@/components/animated-link';
import useImageLoad from '@/hooks/useImageLoad';

/**
 * MapCard popover that appears above a map pin.
 * @param {Object} props
 * @param {string} props.location - The pin location
 * @param {string} props.text - Card body text
 * @param {{ src: string, width: number, height: number, alt: string }} props.image - Card image props
 * @param {string} props.href - Link destination
 * @param {{ left: number, top: number, pinBottom: number }} props.position - Absolute position for the card
 * @param {Function} props.onClose - Called when close animation completes (or instantly for interaction-triggered close)
 */
function MapCard({ location, text, image, href, position, onClose, onTab, onMouseEnter, onMouseLeave, autoFocus = true }) {
  const dialogRef = useRef(null);
  const cardRef = useRef(null);
  const linkRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Clamp card position within viewport after render, flip below pin if no room above
  useLayoutEffect(() => {
    const el = dialogRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const gutter = 24;

    // If card is wider than viewport minus gutters, center it horizontally
    if (rect.width > window.innerWidth - gutter * 2) {
      el.style.left = `${gutter}px`;
      el.style.right = `${gutter}px`;
      el.style.width = `${window.innerWidth - gutter * 2}px`;
    } else {
      // Clamp horizontally
      if (rect.left < gutter) {
        el.style.left = `${gutter}px`;
      }
      if (rect.right > window.innerWidth - gutter) {
        el.style.left = `${window.innerWidth - gutter - rect.width}px`;
      }
    }

    // Re-measure after horizontal adjustments
    const updatedRect = el.getBoundingClientRect();

    // If card overflows top, flip below the pin
    if (updatedRect.top < gutter) {
      el.style.top = `${position.pinBottom}px`;
      el.classList.remove('-translate-y-full');
    }
  }, [position]);

  const leave = () => {
    const tl = gsap.timeline({
      defaults: { ease: 'power2.in' },
      onComplete: () => onCloseRef.current(),
    });

    tl.to(cardRef.current, { opacity: 0, y: 10, duration: 0.2 });

    return tl;
  };

  useGSAP(() => {
    const mm = gsap.matchMedia();

    gsap.killTweensOf(cardRef.current);

    mm.add('(prefers-reduced-motion: no-preference)', () => {
      gsap.fromTo(
        cardRef.current,
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' }
      );
    });

    mm.add('(prefers-reduced-motion: reduce)', () => {
      gsap.set(cardRef.current, { opacity: 1 });
    });
  }, []);

  useEffect(() => {
    if (autoFocus) linkRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        leave();
        return;
      }

      if (e.key === 'Tab' && dialogRef.current?.contains(document.activeElement)) {
        e.preventDefault();
        onTab?.(e.shiftKey);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const {imageRef, onLoad} = useImageLoad()

  return (
    <div
      ref={dialogRef}
      id="map-card-popover"
      role="dialog"
      aria-modal="false"
      aria-label={`Story from ${location}`}
      className="fixed z-50 -translate-y-full"
      style={{ left: position.left, top: position.top }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div ref={cardRef} className="relative opacity-0">
        <AnimatedLink
          ref={linkRef}
          href={href}
          label={`Read story from ${location}`}
          className="flex gap-x-4 w-88.5 max-w-full bg-main-light rounded-[16px] p-4 min-w-[200px] shadow-[0_4px_10px_0_rgba(0,0,0,0.15)]"
          transitionName="overlay-in"
        >
          <div className="flex flex-col gap-y-2">
            <span className="body-3-small !text-[14px] uppercase">{location}</span>
            <span className="body-3-small !text-[16px]">{text}</span>
          </div>

          <Image unoptimized={true} className="w-22 h-auto aspect-176/208 bg-light grow-1 shrink-0 opacity-0" {...image} ref={imageRef}
            onLoad={onLoad}/>
        </AnimatedLink>
      </div>
    </div>
  );
}

export default memo(MapCard)
